import { useTranslation } from 'react-i18next';
import { AssetsMdmStatus } from '@electricjs/core_entity-client';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useUpdateDeviceMdmStatusOverrideMutation } from '@/redux/slices/deviceApiSlice';
import { assetApiSlice } from '@/redux/slices/assetApiSlice';
import { useAppDispatch } from '@/redux/store';
import { Asset } from '@/types/assets';

type UseUpdateMdmStatusOverrideResult = {
  markNotApplicable: (
    asset: Asset,
    messages?: { success?: string; error?: string }
  ) => Promise<boolean>;
  revokeExemption: (asset: Asset) => Promise<void>;
  isLoading: boolean;
};

export const useUpdateMdmStatusOverride = (
  organizationId: string
): UseUpdateMdmStatusOverrideResult => {
  const { t } = useTranslation('assets');
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const dispatch = useAppDispatch();
  const [updateDeviceMdmStatusOverride, { isLoading }] =
    useUpdateDeviceMdmStatusOverrideMutation();

  const markNotApplicable = async (
    asset: Asset,
    messages?: { success?: string; error?: string }
  ): Promise<boolean> => {
    try {
      await updateDeviceMdmStatusOverride({
        organizationId,
        assetId: asset.id,
        mdmStatusOverride: AssetsMdmStatus.NotApplicable,
      }).unwrap();
      dispatch(
        assetApiSlice.util.updateQueryData(
          'getAsset',
          { organizationId, assetId: asset.id },
          draft => {
            draft.mdmStatusOverride = AssetsMdmStatus.NotApplicable;
            if (draft.device) {
              draft.device.mdmStatus = AssetsMdmStatus.NotApplicable;
            }
          }
        )
      );
      showSuccessToast({
        id: `mdm-not-applicable-success-${asset.id}`,
        stack: true,
        message:
          messages?.success ?? t('assetDetailsButtonMenu.exemptFromMdmSuccess'),
      });
      return true;
    } catch {
      showErrorToast({
        id: `mdm-not-applicable-error-${asset.id}`,
        stack: true,
        message:
          messages?.error ?? t('assetDetailsButtonMenu.exemptFromMdmError'),
      });
      return false;
    }
  };

  const revokeExemption = async (asset: Asset): Promise<void> => {
    try {
      await updateDeviceMdmStatusOverride({
        organizationId,
        assetId: asset.id,
        mdmStatusOverride: null,
      }).unwrap();
      dispatch(
        assetApiSlice.util.updateQueryData(
          'getAsset',
          { organizationId, assetId: asset.id },
          draft => {
            draft.mdmStatusOverride = undefined;
            if (draft.device) {
              draft.device.mdmStatus = undefined;
            }
          }
        )
      );
      showSuccessToast({
        id: `revoke-mdm-exemption-success-${asset.id}`,
        stack: true,
        message: t('assetDetailsButtonMenu.revokeMdmExemptionSuccess'),
      });
    } catch {
      showErrorToast({
        id: `revoke-mdm-exemption-error-${asset.id}`,
        stack: true,
        message: t('assetDetailsButtonMenu.revokeMdmExemptionError'),
      });
    }
  };

  return { markNotApplicable, revokeExemption, isLoading };
};
